import { useCallback, useState } from 'react'
import { CreditCard, Loader2, ShieldCheck } from 'lucide-react'
import { verifyOrder } from '@/api/billing'
import type { SiteRegisterResult } from '@/api/website'

/**
 * The payment step of a paid application registration.
 *
 * The account already exists at this point but holds NO roles, so it can see
 * nothing. Paying is what materializes its access — which means abandoning this
 * screen leaves a pending account the visitor can come back and finish, rather
 * than either free access or a lost sign-up.
 */

interface GatewayInstance {
  open: () => void
  on: (event: string, cb: (r: unknown) => void) => void
}

function gateway(): (new (options: Record<string, unknown>) => GatewayInstance) | undefined {
  return (window as unknown as {
    Razorpay?: new (options: Record<string, unknown>) => GatewayInstance
  }).Razorpay
}

const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js'

function loadScript(): Promise<boolean> {
  if (gateway()) return Promise.resolve(true)
  return new Promise((resolve) => {
    let script = document.querySelector<HTMLScriptElement>(`script[src="${RAZORPAY_SRC}"]`)
    if (!script) {
      script = document.createElement('script')
      script.src = RAZORPAY_SRC
      script.async = true
      document.body.appendChild(script)
    }
    script.addEventListener('load', () => resolve(true), { once: true })
    script.addEventListener('error', () => resolve(false), { once: true })
    if (gateway()) resolve(true)
  })
}

export default function SitePaymentStep({
  appName, pending, onPaid, onCancel,
}: {
  appName: string
  pending: NonNullable<SiteRegisterResult['order']>
  onPaid: () => void
  onCancel: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pay = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      if (!(await loadScript())) {
        setError('Could not load the payment gateway. Check your connection and try again.')
        return
      }
      const keyId = pending.provider_config?.key_id
      if (!keyId) {
        setError('This application\'s payment gateway is not configured. Contact the application owner.')
        return
      }
      const Gateway = gateway()
      if (!Gateway) {
        setError('The payment gateway did not load. Try again.')
        return
      }

      await new Promise<void>((resolve) => {
        const rzp = new Gateway({
          key: keyId,
          order_id: pending.checkout.order_id,
          amount: pending.order.total_minor,
          currency: pending.order.currency,
          name: appName,
          description: 'Application access fee',
          theme: { color: '#1976d2' },
          modal: {
            ondismiss: () => {
              setError('Payment cancelled. Your account is saved — sign in any time to finish paying.')
              resolve()
            },
          },
          handler: async (response: Record<string, string>) => {
            try {
              await verifyOrder({
                order_id: pending.order.id,
                provider_payment_id: response.razorpay_payment_id,
                provider_order_id: response.razorpay_order_id,
                signature: response.razorpay_signature,
              })
              onPaid()
            } catch {
              // The payment may well have succeeded — the webhook settles it
              // regardless, so say what is actually true rather than "failed".
              setError(
                'Payment received but confirmation is still catching up. '
                + 'Try signing in again in a moment.',
              )
            }
            resolve()
          },
        })
        rzp.on('payment.failed', (r: unknown) => {
          const failure = r as { error?: { description?: string } }
          setError(failure?.error?.description || 'The payment was declined.')
          resolve()
        })
        rzp.open()
      })
    } finally {
      setBusy(false)
    }
  }, [appName, onPaid, pending])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 4 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <ShieldCheck size={20} style={{ color: 'var(--color-accent)', flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 15, lineHeight: 1.5 }}>
          <strong>Almost there.</strong> {appName} charges a one-time access fee.
          Your account has been created — paying gives it access.
        </div>
      </div>

      <div style={{
        padding: '12px 14px', borderRadius: 8,
        background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 14 }}>Access fee</span>
        <strong style={{ fontSize: 19 }}>{pending.order.display_total}</strong>
      </div>

      {error && (
        <div style={{
          fontSize: 13, padding: '8px 10px', borderRadius: 6,
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-danger, #d64545)',
          color: 'var(--color-text)',
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
          Later
        </button>
        <button type="button" className="btn btn-primary" onClick={pay} disabled={busy}>
          {busy ? <Loader2 size={14} className="spin" /> : <CreditCard size={14} />}
          {' '}Pay {pending.order.display_total}
        </button>
      </div>

      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0 }}>
        Cancelling keeps your account — sign in later to finish paying and get access.
      </p>
    </div>
  )
}
